import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Eye, MousePointerClick, Users, Activity } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
export function Slide2KPIs({
  index
}: SlideProps) {
  const funnel = [{
    value: '144k',
    label: 'Google Impressions',
    icon: Eye,
    color: 'text-[var(--brand-green-lighter)]',
    bg: 'bg-[var(--brand-green-lighter)]',
    height: 'h-full'
  }, {
    value: '16k',
    label: 'DERMAWorld Page Views',
    icon: MousePointerClick,
    color: 'text-[var(--brand-green-light)]',
    bg: 'bg-[var(--brand-green-light)]',
    height: 'h-[60%]'
  }, {
    value: '232',
    label: 'Unique Visitors',
    icon: Users,
    color: 'text-red-500',
    bg: 'bg-red-500',
    height: 'h-[4%]',
    pulse: true
  }, {
    value: '1,662',
    label: 'High-Value Actions',
    icon: Activity,
    color: 'text-[var(--brand-green)]',
    bg: 'bg-[var(--brand-green)]',
    height: 'h-[10%]'
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-128">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.5rem,2.5vw,2.5rem)] flex-shrink-0" data-id="element-129">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-130">
          What the KPI funnel tells us
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] max-w-5xl" data-id="element-131">
          Visibility is strong, but meaningful engagement is{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-132">concentrated.</span>
        </h2>
      </motion.div>

      <div className="flex-1 grid grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)] min-h-0" data-id="element-133">
        <div className="col-span-7 flex flex-col min-h-0" data-id="element-134">
          <div className="flex items-center gap-2 mb-6 flex-shrink-0" data-id="element-135">
            <TrendingDown className="w-5 h-5 text-[var(--muted)]" data-id="element-136" />
            <span className="text-[var(--muted)] text-[clamp(0.8rem,1vw,1rem)] font-semibold uppercase tracking-wide" data-id="element-137">
              Current Digital Funnel
            </span>
          </div>

          <div className="flex-1 grid grid-cols-4 gap-4 items-end min-h-0 relative" data-id="element-138">
            {/* Highlight the drop-off */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 1.2,
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none" data-id="element-139">
              <div className="bg-red-500 text-white text-[clamp(0.75rem,1vw,0.85rem)] font-bold px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap flex items-center gap-1.5 border-2 border-[var(--bg)]" data-id="element-140">
                <TrendingDown className="w-4 h-4" data-id="element-141" />
                98.5% Drop-off
              </div>
            </motion.div>

            {funnel.map((r, i) => {
            const Icon = r.icon;
            return <div key={i} className="h-full flex flex-col justify-end min-h-0" data-id="element-142">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4 + i * 0.2,
                duration: 0.4
              }} className="mb-3 flex-shrink-0 flex flex-col items-center text-center" data-id="element-143">
                    <Icon className={`w-6 h-6 mb-2 ${r.color}`} data-id="element-144" />
                    <p className={`font-bold text-[clamp(1.5rem,2.5vw,2rem)] leading-none ${r.color} ${r.pulse ? 'animate-pulse' : ''}`} data-id="element-145">
                      {r.value}
                    </p>
                    <p className="text-[clamp(0.65rem,0.85vw,0.85rem)] font-bold text-[var(--muted)] mt-2 h-8 flex items-center justify-center" data-id="element-146">
                      {r.label}
                    </p>
                  </motion.div>
                  <div className="flex-1 flex flex-col justify-end min-h-0 relative" data-id="element-147">
                    <motion.div initial={{
                  scaleY: 0
                }} animate={{
                  scaleY: 1
                }} transition={{
                  delay: 0.5 + i * 0.2,
                  duration: 0.6,
                  ease: [0.4, 0, 0.2, 1]
                }} style={{
                  transformOrigin: 'bottom'
                }} className={`${r.height} ${r.bg} rounded-t-xl w-full min-h-[4px]`} data-id="element-148" />
                  </div>
                </div>;
          })}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="col-span-5 bg-[var(--surface)] rounded-2xl p-[clamp(1.5rem,2vw,2rem)] flex flex-col justify-center border border-black/5 gap-6" data-id="element-149">
          <p className="text-[var(--fg)] text-[clamp(1.1rem,1.4vw,1.3rem)] font-medium leading-relaxed" data-id="element-150">
            The large contrast between 16k page views and 232 unique visitors
            suggests that engagement is concentrated among a small group of
            repeat users.
          </p>
          <div className="bg-white p-5 rounded-xl border border-black/5" data-id="element-151">
            <p className="text-[var(--brand-green)] font-bold text-lg mb-2" data-id="element-152">
              The Key Interpretation
            </p>
            <p className="text-[var(--fg)] font-medium" data-id="element-153">
              The issue is not quantity of traffic. The issue is quality of
              engagement.
            </p>
          </div>
          <p className="text-[var(--muted)] text-[clamp(1rem,1.1vw,1.1rem)] leading-relaxed" data-id="element-154">
            This indicates that the current journey may not be creating enough
            relevance or motivation for a broader group of HCPs to engage
            meaningfully.
          </p>
        </motion.div>
      </div>
    </div>;
}